import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { loadCSS, removeCSS } from "../utils/domAssets.js";
import toast from "../utils/toast.js";
import { signup } from "../services/authService.js";

export default function SignupCustomer() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [termsChecked, setTermsChecked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadStyles() {
      await Promise.all([
        loadCSS(
          "https://fonts.googleapis.com/css2?family=Poppins:wght@500;600;700;800&family=Inter:wght@400;500;600;700&display=swap",
          "website-fonts",
        ),
        loadCSS("/assets/website/css/style.css", "website-css"),
        loadCSS("/assets/website/css/signup.css", "signup-css"),
      ]);

      if (isMounted) {
        setIsLoading(false);
      }
    }

    loadStyles();

    return () => {
      isMounted = false;
      removeCSS("website-fonts");
      removeCSS("website-css");
      removeCSS("signup-css");
    };
  }, []);

  function validate() {
    if (name.trim().length < 2) {
      toast.error("Please enter your full name.");
      return false;
    }
    if (!/^\d{10}$/.test(mobile.trim())) {
      toast.error("Enter a valid 10-digit mobile number.");
      return false;
    }
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      toast.error("Enter a valid email address.");
      return false;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return false;
    }
    if (confirmPassword !== password) {
      toast.error("Passwords do not match.");
      return false;
    }
    if (!termsChecked) {
      toast.error("Please accept the Terms of Service to continue.");
      return false;
    }
    return true;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const res = await signup({
        name: name.trim(),
        mobile: mobile.trim(),
        email: email.trim() || undefined,
        password,
        role: "customer",
      });

      if (!res.success) {
        toast.error(res.message || "Something went wrong. Please try again.");
        return;
      }

      toast.success(`${res.message || "Account created"} — redirecting to login...`);
      navigate("/login_customer");
    } catch (err) {
      toast.error(err.response?.data?.message || "Network error — please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (isLoading) {
    return (
      <div className="website-loader">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="auth-body">
      <div className="auth-shell signup-shell">
        <aside className="auth-aside">
          <Link to="/" className="brand brand-542587">
            <img
              src="/assets/website/images/logo.svg"
              alt="RouteRider logo"
              className="brand-logo"
            />
            <span className="brand-name light">
              Route<span className="accent-text">Rider</span>
            </span>
          </Link>
          <img
            src="/assets/website/images/auth-signup-illustration.svg"
            alt=""
            className="aside-illustration"
            aria-hidden="true"
          />
          <h2>Join thousands of riders</h2>
          <ul className="aside-benefits">
            <li>🎟️ Book a seat or a full cab, whichever suits your trip</li>
            <li>💰 Fixed, upfront fares — no surge, ever</li>
            <li>🪪 ID-verified drivers &amp; co-riders</li>
            <li>📍 Track your ride live from pickup to drop-off</li>
          </ul>
          <p className="aside-quote">
            "Signed up in under a minute and booked my first shared seat the
            same day." — Riya S.
          </p>
        </aside>

        <section className="auth-form-panel">
          <div className="auth-form-wrap">
            <div className="auth-mobile-brand hide-desktop">
              <img
                src="/assets/website/images/logo.svg"
                alt="RouteRider logo"
                className="brand-logo"
              />
              <span className="brand-name">
                Route<span className="accent-text">Rider</span>
              </span>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              <div className="field">
                <label htmlFor="suName">Full name</label>
                <input
                  type="text"
                  id="suName"
                  name="name"
                  placeholder="e.g. Riya Sharma"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="field field-84">
                <label htmlFor="suMobile">Customer mobile number</label>
                <input
                  type="tel"
                  id="suMobile"
                  name="mobile"
                  placeholder="10-digit mobile number"
                  inputMode="numeric"
                  maxLength={10}
                  autoComplete="tel"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                />
              </div>
              <div className="field field-84">
                <label htmlFor="suEmail">Email</label>
                <input
                  type="email"
                  id="suEmail"
                  name="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="field field-84">
                <label htmlFor="suPassword">Password</label>
                <div className="password-wrap">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="suPassword"
                    name="password"
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="pw-toggle"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? "🙈" : "👁"}
                  </button>
                </div>
              </div>
              <div className="field field-84">
                <label htmlFor="suConfirm">Confirm password</label>
                <div className="password-wrap">
                  <input
                    type={showConfirm ? "text" : "password"}
                    id="suConfirm"
                    name="confirmPassword"
                    placeholder="Re-enter your password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="pw-toggle"
                    aria-label={showConfirm ? "Hide password" : "Show password"}
                    onClick={() => setShowConfirm((v) => !v)}
                  >
                    {showConfirm ? "🙈" : "👁"}
                  </button>
                </div>
              </div>

              <label className="checkbox-row cus-1-checkbox-row">
                <input
                  type="checkbox"
                  id="suTerms"
                  checked={termsChecked}
                  onChange={(e) => setTermsChecked(e.target.checked)}
                />
                <span>
                  I agree to RouteRider's <a href="#">Terms of Service</a> and{" "}
                  <a href="#">Privacy Policy</a>.
                </span>
              </label>
              <button
                type="submit"
                className="btn btn-primary btn-block"
                disabled={submitting}
              >
                {submitting ? "Creating account..." : "Create account →"}
              </button>
            </form>
            <p className="auth-switch">
              Already have an account? <Link to="/login_customer">Log in</Link>
            </p>
            <p className="auth-switch">
              <Link to="/">← Back to home</Link>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
